import Stack from "./stack";
import Queue from "./queue"
import Step from "./step";

export default class Rpn {

    constructor(expression) {
        this.expression = expression.trim();
        this.error = "";
    }

    static operators = ["+", "-", "*", "/", "^", "sin", "cos", "abs"];
      
    static getPriority(operator)
    {
        switch (operator)
        {
            case "+":
            case "-": return 1;

            case "*":
            case "/": return 2;

            case "^": return 3;

            case "sin":
            case "cos":
            case "abs": return 4;

            default: return -1;
        }
    }

    static splitString(str)
    {
        let operators = Rpn.operators.slice();
        operators.push('(');
        operators.push(')');

        for (let op of operators) {
            str = str.replaceAll(op, ' ' + op + ' ');
        }

        str = str.replace(/\s+/g, ' ').trim();
        return str.split(' ');
    }

    static fromInfixNotation(infixNotation)
    {
        let stack = new Stack();
        let output = "";
    
        var elements = Rpn.splitString(infixNotation);
    
        for (let i = 0; i < elements.length; )
        {
            let s = elements[i];
            if (s === '(') {
                stack.push(s);
            }
            else if (s === ')')
            {
                let peak;
                while (stack.length > 0 && ((peak = stack.peak.val) !== '(')) {
                    peak = stack.pop();
                    output += ' ' + peak;
                }
    
                if (peak === undefined) {
                    output = undefined;
                    break;
                }
    
                stack.pop();
            }
            else if (s.isNumeric()) {
                output += ' ' + s;
            }
            else if (stack.length === 0 || stack.peak.val === '(') {
                stack.push(s);
            }
            else
            {
                if (Rpn.getPriority(stack.peak.val) < Rpn.getPriority(s)) {
                    stack.push(s);
                }
                else {
                    output += ' ' + stack.pop();
                    continue;
                }
            }
            i++;
        }
    
        while (stack.length > 0)
        {
            if (stack.peak.val === '(') {
                output = undefined;
                break;
            }
    
            output += ' ' + stack.pop();
        }

        return output ? new Rpn(output.trim()) : null;
    }
    
    evaluate() {
        this.history = new Queue();
        let num = -1;
        let stack = new Stack();
        let ops = Queue.fromArray(this.expression.replace(',', '.').split(' '));
        let operands = new Stack();
        let operator = undefined;
        let result = undefined;

        let log = () => this.history.enqueue(new Step(++num, ops.clone(), stack.clone(), operator, operands.clone(), this.error, result ));

        log();

        if ((this.expression ?? "") === "") {
            result = "empty expression";
            log();
            return undefined;
        }

        while (ops.length > 0 && ((this.error ?? "") === "")) {
            let x = ops.dequeue();

            if (!x.isOperator()) {
                operator = undefined;
                if (!x.isNumeric()) {
                    this.error = "not numeric value";
                    log();
                    return undefined;
                }

                stack.push(Number(x));
                log();
            }
            else {
                operator = x;
                if (stack.length < 1) {
                    this.error = "expression error";
                    log();
                    return undefined;
                }
                log();
                operands.push(stack.pop());
                log();

                if (operator.isBinaryOperator() && !(operator === '-' && (stack.length === 0))) {
                    if (stack.length === 0) {
                        this.error = "expression error";
                        log();
                        return undefined;
                    }
                    operands.push(stack.pop());
                    log();
                }
                
                switch (operator) {
                    case "+": stack.push(operands.pop() + operands.pop()); break;
                    case "-":
                        if (operands.length === 2) {
                            stack.push(operands.pop() - operands.pop()); break;
                        }
                        else {
                            stack.push(-operands.pop()); break;
                        }
                    case "*": stack.push(operands.pop() * operands.pop()); break;
                    case "^": stack.push(Math.pow(operands.pop(), operands.pop())); break;
                    case "sin": stack.push(Math.sin(operands.pop())); break;
                    case "cos": stack.push(Math.cos(operands.pop())); break;
                    case "abs": stack.push(Math.abs(operands.pop())); break;
                    case "/":
                        let divisible = operands.pop();    
                        let divider = operands.pop();
                        if (divider === 0) {
                            this.error = "zero division";
                            log();
                            return undefined;
                        }
                        stack.push(divisible / divider);
                        break;
                    
                    default: {
                        this.error = "unknown operator";
                        log();
                        return undefined;
                    }
                }

                operator = undefined;

                if (ops.length === 0) {
                    if (stack.length === 1) {
                        log();
                        result = stack.pop();
                        log();
                        return result;
                    }
                    else {
                        this.error = "expression error";
                        log();
                        return undefined;
                    }
                }
                else {
                    log();
                }
            }
        }
    }
}


(function(){
    
    var extensions = {
  
      isNumeric: function() {
        return !isNaN(this) && !isNaN(parseFloat(this));
      },
  
      isVar: function()
      {
          return !this.isFloat() && !this.isOperator() && !(['(', ')', ' '].indexOf(this) >= 0);
      },
  
      isOperator: function()
      {
          return Rpn.operators.indexOf(this) >= 0;
      },
  
      isBinaryOperator: function()
      {
          switch (this)
          {
              case "+":
              case "-":
              case "*":
              case "^":
              case "/": return true;
  
              default: return false;
          }
      }
    };
    
    for (var extension in extensions) {
      // eslint-disable-next-line
      String.prototype[extension] = String.prototype[extension] || extensions[extension];
    }
    
  })();